import { useEffect, useRef, useState } from "react"
import { CheckmarkCircleGreen } from "../../../common/atoms/Icons/Chat"
import { classNames } from "../../utils"
import styles from "./Toast.module.scss"

export enum Position {
    top = "top",
    bottom = "bottom",
}

export enum Preset {
    light = "light",
    dark = "dark",
}

export interface ToastProps {
    message: string
    position?: Position
    preset?: Preset
    duration?: number
    showIcon?: boolean
    className?: string
    onClose: () => void
}

const CLOSE_ANIMATION_DURATION = 500

export const Toast: React.FC<ToastProps> = ({
    message,
    position = Position.top,
    preset = Preset.light,
    duration = 2500,
    showIcon = true,
    className,
    onClose,
}) => {
    const [visible, setVisible] = useState(false)
    const onCloseTimerRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        setVisible(true)

        const dismissTimer = setTimeout(() => {
            setVisible(false)

            onCloseTimerRef.current = setTimeout(() => {
                onClose()
                onCloseTimerRef.current = null
            }, CLOSE_ANIMATION_DURATION) // Allow animation to complete before removal
        }, duration)

        return () => {
            clearTimeout(dismissTimer)
            if (onCloseTimerRef.current) {
                clearTimeout(onCloseTimerRef.current)
                onCloseTimerRef.current = null
            }
        }
    }, [duration, onClose, message])

    return (
        <div
            className={classNames(
                styles.toastGlobal,
                styles[position],
                styles[preset],
                {
                    [styles.visible]: visible,
                },
                className ? styles[className] : undefined,
            )}
            role="alert"
            data-testid="toast-container"
            style={
                { "--toast-duration": `${duration}ms` } as React.CSSProperties
            }
        >
            {showIcon && <CheckmarkCircleGreen className={styles.iconCheck} />}
            {message}
        </div>
    )
}
